import { type ReactNode, Suspense } from 'react';
import { Outlet } from 'react-router';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ErrorState } from '@repo/design-system-kit/components/error-state/error-state';
import { Spinner } from '@repo/design-system-kit/components/spinner/spinner';
import cx from 'clsx';
import { ErrorBoundary } from '../../utils/error-boundary';
import styles from './suspense-boundary.module.css';

interface SuspenseBoundaryProps {
  children?: ReactNode;
  loadingFallback?: ReactNode;
  errorFallback?: (props: { onRetry: () => void; error: unknown }) => ReactNode;
  onError?: (error: unknown, info: React.ErrorInfo) => void;
  /**
   * HTTP status codes this boundary already handles as expected outcomes (e.g. a 404
   * redirect via `onError`), so they shouldn't also be reported to Sentry.
   */
  ignoreStatuses?: readonly number[];
}

function ContentWrapper({ children }: { children: ReactNode }) {
  return (
    <div className="l-app-content__wrapper">
      <div className="l-app-content__main l-app-content__main--animated">
        <div className={cx(styles.container, 'l-block__inner')}>{children}</div>
      </div>
    </div>
  );
}

export function DefaultError({ onRetry }: { onRetry: () => void }) {
  const { formatMessage } = useIntl();

  return (
    <ContentWrapper>
      <ErrorState
        data-testid="error-state"
        onRetry={onRetry}
        subtitle={formatMessage({ id: 'generic.server-error.subtitle' })}
        title={formatMessage({ id: 'generic.server-error.title' })}
      />
    </ContentWrapper>
  );
}

export function DefaultLoading() {
  return (
    <ContentWrapper>
      <Spinner size="large" />
    </ContentWrapper>
  );
}

export function SuspenseBoundary({
  children = <Outlet />,
  loadingFallback = <DefaultLoading />,
  errorFallback = DefaultError,
  onError,
  ignoreStatuses,
}: SuspenseBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          fallbackRender={({ resetErrorBoundary, error }) => {
            // Render DefaultError as a component to allow hooks to work
            if (errorFallback === DefaultError) {
              return <DefaultError onRetry={resetErrorBoundary} />;
            }
            return errorFallback({ onRetry: resetErrorBoundary, error });
          }}
          ignoreStatuses={ignoreStatuses}
          onError={onError}
          onReset={reset}
        >
          <Suspense fallback={loadingFallback}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
